import { FC, useMemo, useRef, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { Button, useToast } from "@racoon/ui";
import { BrandIcPlus } from "@racoon/icons";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { getEnumName } from "@racoon/lib";
import {
  useDeleteContainerGroupScheduleMutation,
  useListContainerGroupSchedulesQuery,
} from "~/shared/api/container-group-schedule/container-group-schedule.grpc";
import { containerGroupName, periodText } from "../lib/schedule-label";
import { ScheduleCard } from "./schedule-card";
import { ScheduleEditorModal } from "./schedule-editor.modal";

type Props = {
  locationId: number | string;
  containerGroups: ConnectProto.ContainerGroup[];
};

/** The location's schedule table rows, one card each, with the editor. */
export const ContainerGroupSchedules: FC<Props> = ({ locationId, containerGroups }) => {
  const t = useT();
  const { toast } = useToast();
  // currentData: a switched location must not show the previous one's schedules.
  const { currentData: data, isError } = useListContainerGroupSchedulesQuery({
    pickupLocationIds: [BigInt(String(locationId))],
  });
  const [deleteSchedule] = useDeleteContainerGroupScheduleMutation();
  // Ids deleted or being deleted: a repeated click before the list refetches sends nothing.
  const deleting = useRef(new Set<string>());
  // undefined: closed; null: a new schedule.
  const [editing, setEditing] = useState<ConnectProto.ContainerGroupSchedule | null>();
  // Cards the user flipped; the first starts open, the rest closed.
  const [flipped, setFlipped] = useState<Record<string, boolean>>({});
  const flip = (id: string) => setFlipped((prev) => ({ ...prev, [id]: !prev[id] }));

  const schedules = useMemo(() => data?.schedules ?? [], [data]);
  const cgById = useMemo(
    () => new Map(containerGroups.map((cg) => [String(cg.id), cg])),
    [containerGroups],
  );
  const scheduleNames = useMemo(
    () =>
      new Map(
        schedules.map((s) => {
          const jobType = getEnumName(ConnectProto.JobTypeSchema, s.jobType);
          return [
            String(s.id),
            [
              containerGroupName(cgById.get(String(s.containerGroupId)), t),
              jobType && t(Namespace.Enum, jobType as TranslationKey),
              periodText(s.validFrom, s.validTo, t),
            ]
              .filter(Boolean)
              .join(" · "),
          ];
        }),
      ),
    [schedules, cgById, t],
  );

  const handleDelete = async (schedule: ConnectProto.ContainerGroupSchedule) => {
    const id = String(schedule.id);
    if (deleting.current.has(id)) return;
    if (!window.confirm(t(Namespace.PickupLocation, "schedules.deleteConfirm"))) return;
    deleting.current.add(id);
    // GlobalErrorToaster reports a failure.
    await deleteSchedule({ id: BigInt(id) })
      .unwrap()
      .catch((error) => {
        deleting.current.delete(id);
        throw error;
      });
    toast({ title: t(Namespace.PickupLocation, "schedules.deleted") });
  };

  return (
    <div className="flex flex-col items-start gap-4">
      <Button
        type="button"
        variant="ghost"
        onClick={() => setEditing(null)}
        disabled={containerGroups.length === 0}
        className="h-8 gap-1 px-3 text-sm font-medium text-text-information hover:bg-surface-muted"
      >
        <BrandIcPlus className="size-4" />
        {t(Namespace.PickupLocation, "schedules.create")}
      </Button>

      {isError && (
        <span className="text-sm text-gray-500">
          {t(Namespace.PickupLocation, "schedules.loadFailed")}
        </span>
      )}

      {data && schedules.length === 0 && (
        <span className="text-sm text-gray-500">
          {t(Namespace.PickupLocation, "schedules.empty")}
        </span>
      )}

      {schedules.map((schedule, i) => (
        <ScheduleCard
          key={String(schedule.id)}
          schedule={schedule}
          expanded={(i === 0) !== !!flipped[String(schedule.id)]}
          onToggle={() => flip(String(schedule.id))}
          containerGroup={cgById.get(String(schedule.containerGroupId))}
          onEdit={() => setEditing(schedule)}
          onDelete={() => handleDelete(schedule).catch(() => undefined)}
        />
      ))}

      {editing !== undefined && (
        <ScheduleEditorModal
          onClose={() => setEditing(undefined)}
          containerGroups={containerGroups}
          schedule={editing ?? undefined}
          scheduleNames={scheduleNames}
          number={editing ? schedules.indexOf(editing) + 1 || undefined : undefined}
        />
      )}
    </div>
  );
};
